/**
 * Shared price calculation logic for Cart and Checkout
 */
// Round to 2 decimals (SEK öre) without going up to a whole krona.
const round2 = (v) => Math.round(v * 100) / 100;

export const calculatePricing = (cart, products, shippingConfig, appliedCoupon) => {
  const subtotal = cart.items?.reduce((total, item) => {
    const product = products[item.product_id];
    const itemPrice = item.price || product?.price || 0;
    return total + (itemPrice * (item.quantity || 1));
  }, 0) || 0;

  // FIX: round to 2 decimals (öre) not whole kronor, otherwise a 100% discount
  // on 443.60 kr rounds up to 444 kr and the total goes negative (-0.40 kr).
  const globalDiscountAmount = shippingConfig.discount_enabled && shippingConfig.discount_percent > 0
    ? round2(subtotal * shippingConfig.discount_percent / 100)
    : 0;

  const couponDiscountAmount = appliedCoupon
    ? round2(subtotal * appliedCoupon.discount_percent / 100)
    : 0;

  // Never discount more than the subtotal — protects against 100%+ codes.
  const bestDiscount = Math.min(subtotal, Math.max(globalDiscountAmount, couponDiscountAmount));

  const discountLabel = couponDiscountAmount >= globalDiscountAmount && appliedCoupon
    ? `Rabattkod ${appliedCoupon.code} (${appliedCoupon.discount_percent}%)`
    : `Rabatt (${shippingConfig.discount_percent}%)`;

  const discountAmount = bestDiscount;
  const afterDiscount = round2(subtotal - discountAmount);

  let shipping = shippingConfig.shipping_cost;
  if (!shippingConfig.shipping_enabled) {
    shipping = 0;
  } else if (shippingConfig.free_shipping_threshold > 0 && afterDiscount >= shippingConfig.free_shipping_threshold) {
    shipping = 0;
  }

  const total = round2(afterDiscount + shipping);

  const vatAmount = shippingConfig.tax_enabled
    ? round2(total * shippingConfig.tax_rate / (100 + shippingConfig.tax_rate))
    : 0;

  return { subtotal, discountAmount, discountLabel, afterDiscount, shipping, total, vatAmount };
};


/**
 * Compute the lowest available price for a product across all its variants.
 * Returns { lowest, hasMultiplePrices } so the UI can decide whether to show "Från X kr".
 */
export const getProductPriceRange = (product) => {
  if (!product) return { lowest: 0, hasMultiplePrices: false };

  const prices = [];

  if (Array.isArray(product.size_quality_prices) && product.size_quality_prices.length > 0) {
    for (const row of product.size_quality_prices) {
      if (typeof row.price === 'number' && row.price > 0) prices.push(row.price);
    }
  }

  if (Array.isArray(product.variants)) {
    for (const v of product.variants) {
      if (typeof v.price === 'number' && v.price > 0) prices.push(v.price);
    }
  }

  if (prices.length === 0 && typeof product.price === 'number') {
    prices.push(product.price);
  }

  if (prices.length === 0) return { lowest: 0, hasMultiplePrices: false };

  const lowest = Math.min(...prices);
  const highest = Math.max(...prices);
  return { lowest, hasMultiplePrices: lowest !== highest };
};

export const formatPriceLabel = (product) => {
  const { lowest, hasMultiplePrices } = getProductPriceRange(product);
  const formatted = `${lowest.toLocaleString('sv-SE')} kr`;
  return hasMultiplePrices ? `Från ${formatted}` : formatted;
};

/**
 * Returns true when a product's sale is currently active based on
 * optional sale_starts_at / sale_ends_at ISO datetime strings. When
 * neither is set, the sale is always considered active.
 */
export const isSaleActive = (product) => {
  if (!product) return false;
  const now = Date.now();
  const starts = product.sale_starts_at ? Date.parse(product.sale_starts_at) : null;
  const ends = product.sale_ends_at ? Date.parse(product.sale_ends_at) : null;
  if (starts && !Number.isNaN(starts) && now < starts) return false;
  if (ends && !Number.isNaN(ends) && now > ends) return false;
  return true;
};

/**
 * Compute the "old price" label to display struck-through on product cards.
 * Uses per-size old_price when the product has size_quality_prices with any
 * old_price entries; otherwise falls back to product.old_price. Returns null
 * when there is no discount to display (including when the sale window is
 * outside the current date).
 */
export const getProductOldPriceLabel = (product) => {
  if (!product) return null;
  if (!isSaleActive(product)) return null;

  const { lowest } = getProductPriceRange(product);

  // Per-size old prices
  if (Array.isArray(product.size_quality_prices) && product.size_quality_prices.length > 0) {
    const olds = product.size_quality_prices
      .map((row) => (row.old_price != null && row.old_price !== '' ? Number(row.old_price) : null))
      .filter((v) => v != null && !Number.isNaN(v) && v > 0);
    if (olds.length > 0) {
      const lowestOld = Math.min(...olds);
      if (lowestOld > lowest) return `${lowestOld.toLocaleString('sv-SE')} kr`;
    }
  }

  // Product-level old price fallback
  if (product.old_price != null && product.old_price !== '' && Number(product.old_price) > lowest) {
    return `${Number(product.old_price).toLocaleString('sv-SE')} kr`;
  }
  return null;
};

/**
 * Compute the largest discount % for a product across all price/old_price pairs
 * so the UI can show "-20%". Returns null when there is no active discount.
 */
export const getProductDiscountPercent = (product) => {
  if (!product) return null;
  if (!isSaleActive(product)) return null;

  const pairs = [];

  if (Array.isArray(product.size_quality_prices) && product.size_quality_prices.length > 0) {
    for (const row of product.size_quality_prices) {
      const price = Number(row.price);
      const oldPrice = row.old_price != null && row.old_price !== '' ? Number(row.old_price) : null;
      if (oldPrice && !Number.isNaN(price) && oldPrice > price && price > 0) {
        pairs.push({ price, oldPrice });
      }
    }
  }

  if (pairs.length === 0 && product.old_price != null && product.old_price !== '') {
    const price = Number(product.price);
    const oldPrice = Number(product.old_price);
    if (oldPrice > price && price > 0) pairs.push({ price, oldPrice });
  }

  if (pairs.length === 0) return null;
  const bestPct = Math.max(
    ...pairs.map((p) => Math.round(((p.oldPrice - p.price) / p.oldPrice) * 100))
  );
  return bestPct > 0 ? bestPct : null;
};
